/**
 * MOTOR DO TOUR — o Driver.js por cima da tela real, sem regra de negócio.
 *
 * Quem decide QUAIS passos entram (permissão, idioma) é quem chama. Aqui só se
 * resolve o "como": destacar, esperar a ação da pessoa, trocar de lugar no
 * produto antes do passo e avisar como terminou (`concluido` ou `pulado`).
 */
import { driver, type Config, type Driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import './guided-tour.css'
import { esperarElemento, esperarSumir } from './esperar-elemento'
import type { DesfechoDoMotor, PassoDoTour, RotaDoPasso, RotulosDoTour } from './types'

export interface OpcoesDoMotor {
  passos: PassoDoTour[]
  rotulos: RotulosDoTour
  irPara: (rota: RotaDoPasso) => void
  aoFechar: (desfecho: DesfechoDoMotor) => void
  /** Passo único (aviso): esconde o "1 de 1". */
  semProgresso?: boolean
}

export interface TourAberto {
  fechar: () => void
}

/** Tempo para a tela assentar depois de uma ação antes de destacar o próximo alvo. */
const RESPIRO_MS = 250
/** Espera o Driver terminar de mostrar o balão (e de mexer no foco) antes de `aoMostrar`. */
const APOS_MOSTRAR_MS = 400

function montarPopover(
  p: PassoDoTour,
  ultimo: boolean,
  r: RotulosDoTour,
  unico: boolean,
): DriveStep['popover'] {
  const soFechar = p.avanco.tipo !== 'botao'
  return {
    title: p.titulo,
    description: p.texto,
    side: p.lado,
    align: p.alinhamento,
    popoverClass: p.destaque ? 'axg-tour axg-tour--destaque' : 'axg-tour',
    showButtons: soFechar ? ['close'] : unico ? ['next', 'close'] : ['next', 'previous', 'close'],
    nextBtnText: p.rotuloProximo ?? (ultimo ? r.concluir : r.proximo),
  }
}

/** Quem pediu menos movimento não vê o overlay deslizar nem a página rolar sozinha. */
function pediuMenosMovimento(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/** Liga o gatilho que faz o passo andar sozinho; devolve quem desliga. */
function ligarAvancoDoPasso(p: PassoDoTour, seguirDepois: () => void): () => void {
  const a = p.avanco
  let vivo = true
  let tirarClique: (() => void) | null = null
  const seguir = () => {
    if (vivo) window.setTimeout(seguirDepois, RESPIRO_MS)
  }
  if (a.tipo === 'aparece') void esperarElemento(a.seletor, 600000).then((el) => el && seguir())
  if (a.tipo === 'some') void esperarSumir(a.seletor).then(seguir)
  if (a.tipo === 'clique' && p.alvo) tirarClique = ligarClique(p.alvo, a.esperar, seguir)
  return () => {
    vivo = false
    tirarClique?.()
  }
}

function ligarClique(alvo: string, esperar: string | undefined, seguir: () => void): () => void {
  const aoClicar = (e: Event) => {
    const el = document.querySelector(alvo)
    if (!el || !(e.target instanceof Node) || !el.contains(e.target)) return
    document.removeEventListener('click', aoClicar, true)
    if (esperar) void esperarElemento(esperar).then(seguir)
    else seguir()
  }
  document.addEventListener('click', aoClicar, true)
  return () => document.removeEventListener('click', aoClicar, true)
}

/** Aparência e acessibilidade do balão (sem comportamento de fluxo). */
function configuracaoVisual(op: OpcoesDoMotor): Config {
  const semMovimento = pediuMenosMovimento()
  return {
    animate: !semMovimento,
    // O Driver põe só um "×" sem nome no botão fechar: o leitor de tela precisa do rótulo traduzido.
    onPopoverRender: (popover) => popover.closeButton.setAttribute('aria-label', op.rotulos.fechar),
    showProgress: !op.semProgresso,
    progressText: op.rotulos.progresso,
    prevBtnText: op.rotulos.voltar,
    doneBtnText: op.rotulos.concluir,
    overlayColor: '#000',
    overlayOpacity: 0.62,
    stagePadding: 6,
    stageRadius: 12,
    allowClose: true,
    smoothScroll: !semMovimento,
    disableActiveInteraction: false,
    // O teclado é do motor: o do Driver ouve Esc no documento inteiro (fechava o tour junto com o
    // modal de convite) e a seta para a direita pulava passos que exigem uma ação.
    allowKeyboardControl: false,
  }
}

/** Diálogo da própria aplicação (o balão do Driver também é `dialog`, por isso fica de fora). */
const DIALOGO_DA_TELA = '[role="dialog"]:not(#driver-popover-content)'

/**
 * Esc fecha o tour, MAS não quando há um diálogo da tela aberto: ele é o dono daquele Esc (o modal
 * de convite fecha e o tour segue para o passo seguinte). Fase de captura: roda antes de o diálogo
 * fechar a si mesmo, quando ele ainda está na tela. Devolve quem desliga o ouvinte.
 */
function ouvirEsc(aoEsc: () => void): () => void {
  const ouvinte = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    if (document.querySelector(DIALOGO_DA_TELA)) return
    aoEsc()
  }
  document.addEventListener('keydown', ouvinte, true)
  return () => document.removeEventListener('keydown', ouvinte, true)
}

export function abrirTour(op: OpcoesDoMotor): TourAberto {
  let d: Driver | null = null
  let encerrado = false
  let pararEsc: () => void = () => undefined
  let desligar: (() => void) | null = null
  const limpar = () => {
    desligar?.()
    desligar = null
  }

  /**
   * Único caminho de saída. O Driver só chama `onDestroyed` quando já tem um elemento ativo: quem
   * fecha logo que o balão aparece (durante a animação de entrada) fazia o balão sumir SEM o
   * chamador saber, e o tour/aviso nunca era gravado como visto (E2E real, intermitente).
   * Idempotente: fechar por X, Esc, "Entendi", `fechar()` ou o fim do roteiro avisa UMA vez.
   */
  function encerrar(desfecho: DesfechoDoMotor) {
    if (encerrado) return
    encerrado = true
    pararEsc()
    d?.destroy()
    limpar()
    if (focoAnterior?.isConnected) focoAnterior.focus()
    op.aoFechar(desfecho)
  }

  async function irParaPasso(i: number) {
    limpar()
    const p = op.passos[i]
    if (!p || !d) return
    if (p.rota) op.irPara(p.rota)
    await p.prepara?.()
    if (p.alvo) await esperarElemento(p.alvo)
    // Ouve o clique/evento ANTES de o balão aparecer, e não quando o Driver termina a animação de
    // entrada (`onHighlighted`): quem toca no alvo assim que o balão surge chegava antes do
    // ouvinte e o passo ficava esperando um clique que já tinha acontecido (E2E real, 1 de 1).
    ligarAvanco(p, i)
    window.setTimeout(() => {
      if (!d) return
      if (d.isActive()) d.moveTo(i)
      else d.drive(i)
      if (p.aoMostrar) window.setTimeout(p.aoMostrar, APOS_MOSTRAR_MS)
    }, p.rota ? RESPIRO_MS : 0)
  }

  function avancar(i: number) {
    op.passos[i]?.aoAvancar?.()
    if (i >= op.passos.length - 1) {
      encerrar('concluido')
      return
    }
    void irParaPasso(i + 1)
  }

  function ligarAvanco(p: PassoDoTour, i: number) {
    limpar()
    desligar = ligarAvancoDoPasso(p, () => avancar(i))
  }

  const passos: DriveStep[] = op.passos.map((p, i) => ({
    element: p.alvo,
    popover: {
      ...montarPopover(p, i === op.passos.length - 1, op.rotulos, op.semProgresso === true),
      onNextClick: () => avancar(i),
      onPrevClick: () => void irParaPasso(Math.max(0, i - 1)),
    },
  }))

  // Quem usa teclado ou leitor de tela volta para onde estava quando o tour acabar.
  const focoAnterior = document.activeElement instanceof HTMLElement ? document.activeElement : null
  d = driver({
    ...configuracaoVisual(op),
    steps: passos,
    // X, Esc e clique fora chegam aqui ANTES de o Driver destruir qualquer coisa.
    onDestroyStarted: () => encerrar('pulado'),
  })
  pararEsc = ouvirEsc(() => encerrar('pulado'))
  void irParaPasso(0)
  return { fechar: () => encerrar('pulado') }
}
